<template>
  <div class="message-notification" v-if="isUserLoggedIn()">
    <el-popover placement="bottom" width="350" trigger="click">
      <div slot="reference" class="message-trigger">
        <el-badge :value="pendingCount" :hidden="pendingCount === 0" class="message-badge">
          <i class="el-icon-bell message-icon" :style="{'color': iconColor}"></i>
        </el-badge>
      </div>

      <el-tabs v-model="activeName" @tab-click="onTabChange">
        <!-- 待处理标签页 -->
        <el-tab-pane :label="`${$parseLabel('待处理')}(${pendingCount})`" name="first">
          <div v-if="isLoadingPending" class="message-loading">
            <i class="el-icon-loading"></i>
            <span>{{ $parseLabel('加载中') }}...</span>
          </div>
          <div v-else>
            <div v-if="pendingList.length > 0" class="message-list">
              <div
                v-for="item in pendingList"
                :key="item.id"
                class="message-item"
                @click="handleTodoClick(item)"
              >
                <div class="message-content">
                  <span class="message-title">{{ item.title }}</span>
                  <span class="message-date">{{ formatTime(item.createdTime) }}</span>
                </div>
              </div>
            </div>
            <div v-else class="message-empty">
              <div>{{ $parseLabel('暂无待处理') }}</div>
            </div>
            <!-- 分页 -->
            <el-pagination
              v-if="pendingCount > pageSize"
              :current-page="pendingPage"
              :page-size="pageSize"
              :total="pendingCount"
              @current-change="onPendingPageChange"
              small
              layout="prev, pager, next"
              background
              class="message-pagination"
            />
          </div>
        </el-tab-pane>

        <!-- 已处理标签页 -->
        <el-tab-pane :label="`${$parseLabel('已处理')}(${processedCount})`" name="second">
          <div v-if="isLoadingProcessed" class="message-loading">
            <i class="el-icon-loading"></i>
            <span>{{ $parseLabel('加载中') }}...</span>
          </div>
          <div v-else>
            <div v-if="processedList.length > 0" class="message-list">
              <div
                v-for="item in processedList"
                :key="item.id"
                class="message-item processed"
                @click="handleTodoClick(item)"
              >
                <div class="message-content">
                  <span class="message-title">{{ item.title }}</span>
                  <span class="message-date">{{ formatTime(item.createdTime) }}</span>
                </div>
              </div>
            </div>
            <div v-else class="message-empty">
              <div>{{ $parseLabel('暂无已处理') }}</div>
            </div>
            <!-- 分页 -->
            <el-pagination
              v-if="processedCount > pageSize"
              :current-page="processedPage"
              :page-size="pageSize"
              :total="processedCount"
              @current-change="onProcessedPageChange"
              layout="prev, pager, next"
              small
              background
              class="message-pagination"
            />
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-popover>
  </div>
</template>

<script>
import { getTodoList } from '@/api/todo'

export default {
  name: 'MessageNotification',
  data() {
    return {
      activeName: 'first',
      pendingList: [],
      processedList: [],
      pendingCount: 0,
      processedCount: 0,
      isLoadingPending: false,
      isLoadingProcessed: false,
      pageSize: 6,
      pendingPage: 1,
      processedPage: 1,
      timer: null,
      retryCount: 0,
      MAX_RETRY_COUNT: 3,
      iconColor: '#ffffff' // 默认白色，可以通过props传入
    }
  },
  props: {
    // 可以通过props传入图标颜色，适配不同主题
    messageIconColor: {
      type: String,
      default: '#ffffff'
    }
  },
  watch: {
    messageIconColor: {
      handler(newColor) {
        this.iconColor = newColor
      },
      immediate: true
    },
    // 监听用户登录状态变化
    '$store.state.userData': {
      handler(newUserData, oldUserData) {
        // 如果用户刚刚登录（从无用户信息变为有用户信息）
        if (!oldUserData?.name && newUserData?.name) {
          this.fetchList(0, 1)
          this.startTimer()
        }
        // 如果用户退出登录
        else if (oldUserData?.name && !newUserData?.name) {
          this.stopTimer()
          this.pendingList = []
          this.processedList = []
          this.pendingCount = 0
          this.processedCount = 0
        }
      },
      deep: true
    }
  },
  mounted() {
    // 只有在用户已登录时才获取消息列表
    if (this.isUserLoggedIn()) {
      this.fetchList(0, 1)
      this.startTimer()
    }
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy() {
    this.stopTimer()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    async fetchList(status, page = 1) {
      // 检查用户是否已登录
      if (!this.isUserLoggedIn()) {
        return
      }

      if (status === 0 && this.isLoadingPending) return
      if (status === 1 && this.isLoadingProcessed) return
      
      try {
        if (status === 0) this.isLoadingPending = true
        if (status === 1) this.isLoadingProcessed = true
        
        const response = await getTodoList({ 
          pageNum: page, 
          pageSize: this.pageSize, 
          status 
        })
        
        if (response && response.success) {
          // 兼容不同的数据结构格式
          let list = []
          let pending = 0
          let processed = 0
          
          if (response.data) {
            // 格式1: { list: { list: [...] }, pending: 0, processed: 0 } (het2格式)
            if (response.data.list && response.data.list.list) {
              list = response.data.list.list
              pending = response.data.pending || 0
              processed = response.data.processed || 0
            }
            // 格式2: { list: [...], pending: 0, processed: 0 } (可能的HomePage格式)
            else if (Array.isArray(response.data.list)) {
              list = response.data.list
              pending = response.data.pending || 0
              processed = response.data.processed || 0
            }
            // 格式3: 直接是数组 (简单格式)
            else if (Array.isArray(response.data)) {
              list = response.data
              // 如果没有统计信息，根据status过滤计算
              if (status === 0) {
                pending = list.filter(item => item.status === '0' || item.status === 0).length
                processed = list.filter(item => item.status === '1' || item.status === 1).length
              }
            }
            // 格式4: 分页格式 { records: [...], total: 0 }
            else if (response.data.records) {
              list = response.data.records
              // 这种格式通常没有pending/processed统计，需要单独获取
            }
          }
          
          if (status === 0) {
            this.pendingList = list || []
            this.pendingCount = pending
            this.pendingPage = page
            this.processedCount = processed
          } else {
            this.processedList = list || []
            this.processedCount = processed
            this.processedPage = page
          }
          this.retryCount = 0
        } else {
          throw new Error(response?.message || '获取数据失败')
        }
      } catch (error) {
        // 静默处理错误，不显示弹窗
        console.error('获取消息列表失败:', error)
        
        // 如果是认证错误，停止重试
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.stopTimer()
          return
        }
        
        // 其他错误进行有限重试，但不显示错误提示
        if (this.retryCount < this.MAX_RETRY_COUNT) {
          this.retryCount++
          setTimeout(() => {
            this.fetchList(status, page)
          }, 5000 * this.retryCount)
        }
      } finally {
        if (status === 0) this.isLoadingPending = false
        if (status === 1) this.isLoadingProcessed = false
      }
    },

    onPendingPageChange(page) {
      this.fetchList(0, page)
    },

    onProcessedPageChange(page) {
      this.fetchList(1, page)
    },

    onTabChange(tab) {
      if (tab.name === 'first') {
        this.fetchList(0, 1)
      } else if (tab.name === 'second') {
        this.fetchList(1, 1)
      }
    },

    formatTime(timeStr) {
      if (!timeStr) return ''
      const date = new Date(timeStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    async handleTodoClick(item) {
      try {
        if (!item.url) {
          this.$message({
            type: 'warning',
            message: this.$parseLabel('该消息暂无跳转链接')
          })
          return
        }

        if (item.url.startsWith('http')) {
          window.open(item.url, '_blank')
        } else {
          // 检查路由是否存在
          const route = this.$router.resolve(item.url)
          if (route.resolved.matched.length > 0) {
            this.$router.push(item.url)
          } else {
            this.$message({
              type: 'warning', 
              message: this.$parseLabel('页面不存在或无访问权限')
            })
          }
        }
      } catch (error) {
        this.handleError(error, '跳转页面')
      }
    },

    startTimer() {
      if (!this.isUserLoggedIn()) {
        return
      }
      
      this.fetchList(0, 1)
      this.timer = setInterval(() => {
        if (!document.hidden && this.isUserLoggedIn()) {
          this.fetchList(0, this.pendingPage)
        }
      }, 5 * 60 * 1000) // 5分钟刷新一次
    },

    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    handleVisibilityChange() {
      if (!document.hidden && this.isUserLoggedIn()) {
        this.fetchList(0, this.pendingPage)
      }
    },

    // 检查用户是否已登录
    isUserLoggedIn() {
      // 检查store中的用户信息
      if (this.$store && this.$store.state && this.$store.state.userData && this.$store.state.userData.name) {
        return true
      }
      
      // 检查sessionStorage中的token
      const token = sessionStorage.getItem('access_token')
      return token && token !== ''
    },

    // 错误处理方法
    handleError(error, operation = '操作') {
      console.error(`${operation}失败:`, error)
      
      // 使用HomePage的消息提示方式
      this.$message({
        type: 'error',
        message: `${this.$parseLabel(operation)}失败，请稍后重试`
      })
    }
  }
}
</script>

<style lang="less" scoped>

.message-notification {
  display: flex;
  align-items: center;
  height: 100%;

  .message-trigger {
    display: flex;
    align-items: center;
    height: 100%;
    cursor: pointer;
    padding: 0 10px;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: var(--topMenuHoverBgColor);
    }

    .message-badge {
      display: flex;
      align-items: center;
      position: relative;

      /deep/ .el-badge__content {
        background-color: #f56c6c;
        border: 1px solid #fff;
        font-size: 12px;
        }

      .message-icon {
        font-size: 20px;
        transition: color 0.3s ease;
      }
    }
  }
}

.message-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;

  i {
    margin-bottom: 10px;
    font-size: 24px;
  }
}

.message-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;

  img {
    width: 60px;
    height: 60px;
    margin-bottom: 10px;
  }
}

.message-list {
  max-height: 400px;
  overflow-y: auto;

  .message-item {
    display: flex;
    align-items: center;
    padding: 12px 8px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f5f7fa;
    }

    &:last-child {
      border-bottom: none;
    }

    .message-item-icon {
      width: 20px;
      height: 20px;
      margin-right: 12px;
      flex-shrink: 0;
    }

    .message-content {
      flex: 1;
      min-width: 0;

      .message-title {
        display: block;
        font-size: 13px;
        color: #303133;
        line-height: 1.4;
        margin-bottom: 4px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .message-date {
        font-size: 12px;
        color: #909399;
      }
    }

    &.processed {
      .message-item-icon,
      .message-title {
        opacity: 0.6;
      }
    }
  }
}

.message-pagination {
  display: flex;
  justify-content: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

// 弹窗样式调整
/deep/ .el-popover {
  padding: 0;
  
  .el-tabs {
    .el-tabs__header {
      margin: 0;
      padding: 0 16px;
      background-color: #f8f9fa;
      
      .el-tabs__nav-wrap {
        &::after {
          display: none;
        }
      }
      
      .el-tabs__item {
        font-size: 13px;
        padding: 0 12px;
        
        &.is-active {
          color: var(--topMenuActiveTextColor);
        }
      }
    }
    
    .el-tabs__content {
      padding: 16px;
    }
  }
}

// 自定义滚动条
.message-list::-webkit-scrollbar {
  width: 4px;
}

.message-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.message-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;

  &:hover {
    background: #a8a8a8;
  }
}
</style>
